/**
 * 票券瀏覽歷史 — localStorage 持久化。
 *
 * 設計動機：API 端 orders/tickets 表故意不存 buyer user_id（buyer 為 PII
 * encrypted；見 apps/api/src/modules/tickets/tickets.service.ts:129 註）。
 * 在業主決定「buyer-only-view 契約」前，PWA 的「我的票券」由 client-side
 * history 提供 — checkout 成功 + TicketQR 開啟時 push 一筆。
 *
 * 不持久化任何敏感資料（無 buyer name / email / phone）；僅 ticket_id /
 * ticket_code / order_id / addedAt。Storage key 含 user_id 以避免共用裝置
 * 不同使用者撞看（logout 時 clear）。
 */

const STORAGE_KEY_PREFIX = 'boat:ticket-history:v1';
const MAX_ENTRIES = 50;

export interface TicketHistoryEntry {
  ticketId: string;
  ticketCode?: string;
  orderId?: string;
  /** ISO 8601 timestamp */
  addedAt: string;
}

function storageKey(userId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}:${userId ?? 'anon'}`;
}

function safeRead(userId: string | undefined): TicketHistoryEntry[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (raw === null) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is TicketHistoryEntry =>
        typeof e === 'object' &&
        e !== null &&
        typeof (e as TicketHistoryEntry).ticketId === 'string' &&
        typeof (e as TicketHistoryEntry).addedAt === 'string',
    );
  } catch {
    return [];
  }
}

function safeWrite(userId: string | undefined, entries: TicketHistoryEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(entries));
  } catch {
    // quota exceeded / disabled — silent fail
  }
}

/** 列出當前 user 的 history，最新在前。 */
export function listTicketHistory(userId: string | undefined): TicketHistoryEntry[] {
  return safeRead(userId).sort((a, b) => b.addedAt.localeCompare(a.addedAt));
}

/**
 * Push 一筆（或更新既有）。同 ticketId 視為同筆 — 重新 push 會更新 ticketCode /
 * orderId / addedAt，保持 list 最新。
 */
export function rememberTicket(
  userId: string | undefined,
  entry: Omit<TicketHistoryEntry, 'addedAt'> & { addedAt?: string },
): void {
  const now = entry.addedAt ?? new Date().toISOString();
  const existing = safeRead(userId);
  const without = existing.filter((e) => e.ticketId !== entry.ticketId);
  const next: TicketHistoryEntry = {
    ticketId: entry.ticketId,
    addedAt: now,
    ...(entry.ticketCode !== undefined && { ticketCode: entry.ticketCode }),
    ...(entry.orderId !== undefined && { orderId: entry.orderId }),
  };
  const merged = [next, ...without].slice(0, MAX_ENTRIES);
  safeWrite(userId, merged);
}

/** Logout 時呼叫。 */
export function clearTicketHistory(userId: string | undefined): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}
